/**
 * Builds incident payload for API submission
 * Pure function - no side effects, no React dependencies
 *
 * @param {Object} params - All data needed to build payload
 * @param {string} params.status - "draft" or "submitted"
 * @param {Object} params.formData - All form field values
 * @param {Array} params.selectedParticipants - Selected participant objects
 * @param {Array} params.selectedWorker - Selected worker objects array
 * @param {Object|null} params.selectedOutcome - Selected outcome object or null
 * @param {Array} params.selectedInjuryCauses - Selected injury cause objects
 * @param {string} params.injuryCauseOther - Other injury cause text
 * @param {Array} params.selectedBodyParts - Selected body part IDs
 * @param {Array} params.incidentCategory - Selected incident category objects
 * @param {string} params.incidentCategoryOthers - Other incident category text
 * @param {Array} params.personInvolved - Selected person involved objects
 * @param {string} params.personInvolvedVisitor - Visitor text
 * @param {string} params.personInvolvedVolunteer - Volunteer text
 * @param {string} params.personInvolvedOther - Other person text
 * @param {string} params.incidentDate - Fallback incident date
 * @param {string} params.incidentOverviewDate - Fallback overview date
 * @param {string} params.incidentOverviewTime - Fallback overview time
 * @param {Array} params.witnesses - Legacy witnesses array
 * @param {Array} params.uploadedFiles - Uploaded file URLs
 * @param {string|null} params.signatureData - Signature data URL
 * @param {Function} params.getWatchValue - Function to get watch values (for abuse_check, etc.)
 * @param {string|null} [params.timesheetUuid] - Care Watch timesheet uuid, when promoting
 * @returns {Object} Complete payload object
 */
export const buildIncidentPayload = ({
  status,
  formData,
  selectedParticipants,
  selectedWorker,
  selectedOutcome,
  selectedInjuryCauses,
  injuryCauseOther,
  selectedBodyParts,
  incidentCategory,
  incidentCategoryOthers,
  ndisReferenceNumber,
  personInvolved,
  personInvolvedVisitor,
  personInvolvedVolunteer,
  personInvolvedOther,
  incidentDate,
  incidentOverviewDate,
  incidentOverviewTime,
  witnesses,
  uploadedFiles,
  signatureData,
  getWatchValue,
  timesheetUuid,
}) => {
  return {
    participant_ids:
      selectedParticipants?.length > 0
        ? selectedParticipants.map((p) => p.id || p.value)
        : [],
    worker_ids:
      selectedWorker?.length > 0
        ? selectedWorker.map((w) => w.id || w.value)
        : [],
    incident_date: formData.incident_date || incidentDate,
    incident_time: formData.incident_time,
    location: formData.location,
    description: formData.description,
    outcome_type: selectedOutcome?.value,
    injuries_occurred: formData.injuries_occurred === "yes",
    injury_cause:
      selectedInjuryCauses.length > 0
        ? selectedInjuryCauses.map((c) => c.value)
        : [],
    injury_cause_other: injuryCauseOther || null,
    injury_details: formData.injury_details || null,
    abuse_check: getWatchValue("abuse_check") || null,
    abuse_details:
      getWatchValue("abuse_check") === "yes"
        ? getWatchValue("abuse_details") || null
        : null,
    treatment_administered: getWatchValue("treatment_administered") || null,
    treatment_administrator:
      getWatchValue("treatment_administered") === "yes"
        ? getWatchValue("treatment_administrator") || null
        : null,
    body_parts: selectedBodyParts || null,
    immediate_actions: formData.immediate_actions,
    witnesses: witnesses,
    attachments: uploadedFiles,
    signature: signatureData,
    signatory_name: formData.signatory_name || null,
    status: status,
    // Incident Details fields
    executive_summary: formData.executive_summary || null,
    has_behavior_management_plan: formData.has_behavior_management_plan || null,
    regulated_restrictive_practices_used:
      formData.regulated_restrictive_practices_used || null,
    current_task: formData.current_task || null,
    incident_details_narrative: formData.incident_details_narrative || null,
    conclusion: formData.conclusion || null,
    triggers_circumstances: formData.triggers_circumstances || null,
    immediate_action_taken: formData.immediate_action_taken || null,
    incident_details_witnesses: formData.incident_details_witnesses || [],
    person_involved:
      personInvolved.length > 0 ? personInvolved.map((p) => p.value) : [],
    person_involved_visitor: personInvolvedVisitor || null,
    person_involved_volunteer: personInvolvedVolunteer || null,
    person_involved_other: personInvolvedOther || null,
    person_involved_others: personInvolvedOther || null, // Legacy field for backward compatibility
    incident_category:
      incidentCategory.length > 0 ? incidentCategory.map((c) => c.value) : [],
    incident_category_others: incidentCategoryOthers || null,
    ndis_reference_number: ndisReferenceNumber || null,
    incident_overview_date:
      formData.incident_overview_date || incidentOverviewDate,
    incident_overview_time:
      formData.incident_overview_time || incidentOverviewTime,
    incident_address: formData.incident_address || "",
    incident_location_details: formData.incident_location_details || "",
    person_responsible_notified: formData.person_responsible_notified || "",
    medication_incident_categories: formData.medication_incident_categories,
    medication_other_description: formData.medication_other_description,
    medication_possible_reasons: formData.medication_possible_reasons,
    medication_immediate_action: formData.medication_immediate_action,
    coordinator_notified: formData.coordinator_notified,
    doctor_notified: formData.doctor_notified,
    pharmacist_notified: formData.pharmacist_notified,
    ...(timesheetUuid ? { timesheet_uuid: timesheetUuid } : {}),
    first_aid_given: formData.first_aid_given,
    ambulance_needed: formData.ambulance_needed,
    prn_administered: formData.prn_administered,
    reported_to_police: formData.reported_to_police,
    medical_review_offered: formData.medical_review_offered,
  };
};
